import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  Typography,
  Chip,
  Collapse,
  useMediaQuery,
  Tooltip,
  Checkbox,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import PrintIcon from "@mui/icons-material/Print";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { exportToExcel } from "../../../Utility/Utilitys";

// 🔷 Styled Components

const HeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  // textTransform: "capitalize",
  fontSize: 12,
  letterSpacing: 0.6,
  borderBottom: `1px solid ${theme.palette.divider}`,
  whiteSpace: "nowrap",           // ✅ keeps text on one line
  overflow: "hidden",             // hides overflow if needed
  textOverflow: "ellipsis",       // adds “…” if text is too long
  border:"none !important",
  backgroundColor: "#fff !important",
    color: theme.palette.primary.main,
    padding:"4px !important"
}));

function useDebouncedValue(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

function printTable(tableRef) {
  if (!tableRef.current) return;

  // 1) Clone the table
  const cloned = tableRef.current.cloneNode(true);

  // 2) Remove the filter row (2nd TR in THEAD)
  const thead = cloned.querySelector('thead');
  if (thead && thead.rows && thead.rows.length > 1) {
    thead.deleteRow(1);
  }

  // 3) MATERIALIZE current control values into plain text
  //    TextField (inputs), native selects, and MUI Select (non-native)
  const materializeControls = (root) => {
    // MUI TextField input -> put its value into the cell
    root.querySelectorAll('input.MuiInputBase-input, textarea.MuiInputBase-input').forEach(inp => {
      const td = inp.closest('td,th');
      if (!td) return;
      td.textContent = inp.value ?? '';
    });

    // MUI Select (non-native): displayed value is inside .MuiSelect-select
    root.querySelectorAll('.MuiSelect-select').forEach(el => {
      const td = el.closest('td,th');
      if (!td) return;
      td.textContent = (el.textContent || '').trim();
    });

    // Native <select>: use selected option's text
    root.querySelectorAll('select').forEach(sel => {
      const td = sel.closest('td,th');
      if (!td) return;
      const label = sel.selectedOptions?.[0]?.text ?? sel.value ?? '';
      td.textContent = label;
    });

    // Also convert any adornment-within-TextField layouts (safety)
    root.querySelectorAll('.MuiInputBase-root').forEach(rootEl => {
      const td = rootEl.closest('td,th');
      if (!td) return;
      const input = rootEl.querySelector('input, textarea');
      const selectDisplay = rootEl.querySelector('.MuiSelect-select');
      if (selectDisplay) td.textContent = (selectDisplay.textContent || '').trim();
      else if (input) td.textContent = input.value ?? '';
    });
  };
  materializeControls(cloned);

  // 4) (Optional) mark numeric columns for centering in print
  const headerCells = Array.from(cloned.querySelectorAll('thead tr:first-child th, thead tr:first-child td'));
  const numericColIdx = headerCells
    .map((th, i) => (/no.?of.?items|qty|quantity|amount|page\s*no/i.test(th.textContent || '') ? i : -1))
    .filter(i => i >= 0);
  cloned.querySelectorAll('tbody tr').forEach(tr => {
    numericColIdx.forEach(i => tr.cells[i]?.classList?.add('numeric'));
  });

  // 5) Now it’s safe to strip interactive bits (they’re already materialized)
  cloned.querySelectorAll('input, select, textarea, button').forEach(el => el.remove());
  cloned.querySelectorAll('svg').forEach(el => el.remove());
  cloned.querySelectorAll('thead, thead th').forEach(el => { el.style.position = 'static'; });

  // 6) Print in a clean window
  const win = window.open('', '_blank');
  if (!win) return;
  const css = `
  @page { size: landscape; margin: 10mm; }
  @media print { .AvatarCell { display: none !important; } }

  body {
    font-family: Roboto, system-ui, -apple-system, Segoe UI, Helvetica, Arial;
    padding: 0;
  }

  table {
    width: 100%;
    border-collapse: collapse;  /* ✅ make borders continuous */
  }

  th, td {
    border: 1px solid #444 !important;  /* ✅ visible dark borders */
    padding: 8px 10px !important;
    font-size: 12px;
    vertical-align: middle !important;
  }

  thead th {
    background-color: #f0f0f0 !important; /* ✅ light header shade */
    font-weight: 600;
    text-transform: uppercase;
  }

  tbody td {
    border-bottom: 1px solid #888 !important;
  }

  tbody tr:nth-child(even) td {
    background-color: #fafafa !important;
  }

  /* ✅ Center numeric columns */
  .numeric {
    text-align: center !important;
  }

  /* ✅ Keep text from wrapping awkwardly */
  td, th {
    white-space: nowrap;
  }
`;
  win.document.write(`<!doctype html><html><head><meta charset="utf-8" />
    <title>Print</title><style>${css}</style></head><body></body></html>`);
  win.document.body.appendChild(cloned);
  win.document.close();
  win.focus();
  win.print();
  win.close();
}

 function CustomDatagrid({
  columns,
  rows,
  pageType,
  pageSizeOptions = [10, 25, 50],
  initialPageSize = 10,
  dense = false,
  onRowClick,
  showFilters = false,
  showGlobalSearch = false,
  showPagination = false,
  multiSelect = false,
  onSelectionChange,            // (selectedIds: any[], selectedRows: any[]) => void
  getRowId = (row, idx) => idx ?? row.id ?? row.TransId, // robust ID getter
  height
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [globalQuery, setGlobalQuery] = useState("");
  const [filters, setFilters] = useState(() => Object.fromEntries(columns?.map(c => [c.key, ""])));
  const [sort, setSort] = useState({ key: null, dir: null });
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(0); // zero-based for TablePagination

  /* ✅ selection state (for multiSelect) */
  const [selected, setSelected] = useState(() => new Set());

  const debouncedQuery = useDebouncedValue(globalQuery, 250);
  const tableRef = useRef(null);

  const processedRows = useMemo(() => {
    let data = rows ?? [];

    // Global search
    if (debouncedQuery.trim()) {
      const q = debouncedQuery.toLowerCase();
      const keys = columns?.map(c => c.key);
      data = data.filter(r => keys.some(k => String(r[k] ?? "").toLowerCase().includes(q)));
    }
    // Column filters
    for (const col of columns) {
      const v = filters[col.key];
      if (v && String(v).trim() !== "") {
        const q = String(v).toLowerCase();
        data = data.filter(r => String(r[col.key] ?? "").toLowerCase().includes(q));
      }
    }
    // Sorting
    if (sort.key && sort.dir) {
      const dir = sort.dir === "asc" ? 1 : -1;
      const key = sort.key;
      data = [...data].sort((a, b) => {
        const av = a[key];
        const bv = b[key];
        if (av == null && bv == null) return 0;
        if (av == null) return -1 * dir;
        if (bv == null) return 1 * dir;
        if (!isNaN(av) && !isNaN(bv)) return (Number(av) - Number(bv)) * dir;
        return String(av).localeCompare(String(bv)) * dir;
      });
    }
    return data;
  }, [rows, columns, debouncedQuery, filters, sort]);

  
// effective page size: if pagination is hidden, show everything
const effectivePageSize = showPagination ? pageSize : processedRows.length;

// only slice when pagination is ON
const pageRows = useMemo(() => {
  if (!showPagination) return processedRows;
  const start = page * pageSize;
  return processedRows.slice(start, start + pageSize);
}, [processedRows, page, pageSize, showPagination]);

// (optional) if you still use it somewhere
const pageCount = Math.max(
  1,
  Math.ceil(processedRows.length / (effectivePageSize || 1))
);

useEffect(() => {
  if (!showPagination) setPage(0);
}, [showPagination]);

// keep this existing effect
useEffect(() => { setPage(0); }, [debouncedQuery, JSON.stringify(filters), pageSize]);


  const clearFilters = () => {
    setFilters(Object.fromEntries(columns?.map(c => [c.key, ""])));
    setGlobalQuery("");
    setSort({ key: null, dir: null });
  };

  const sortIcon = (col) => {
    if (!col.sortable) return <UnfoldMoreIcon fontSize="small" sx={{ color: "text.dark"  }} />;
    if (sort.key !== col.key || !sort.dir) return <UnfoldMoreIcon fontSize="small" sx={{ color: "text.dark"  }} />;
    return sort.dir === "asc" ? <ArrowUpwardIcon sx={{ color: "text.dark" }} className="arrow-up" fontSize="small" color="action" /> : <ArrowDownwardIcon sx={{ color: "text.dark" }} className="arrow-down" fontSize="small" color="action" />;
  };

  // remove selections for rows that no longer exist
  useEffect(() => {
    if (!multiSelect) return;
    const ids = new Set(processedRows.map((r, i) => getRowId(r, i)));
    setSelected((prev) => {
      const next = new Set([...prev].filter((id) => ids.has(id)));
      if (onSelectionChange) {
        const selRows = processedRows.filter((r, i) => next.has(getRowId(r, i)));
        onSelectionChange([...next], selRows);
      }
      return next;
    });
  }, [processedRows, multiSelect]);

   /* ---------- selection mechanics ---------- */
  const pageIds = useMemo(
    () => pageRows?.map((r, i) => getRowId(r, i)),
    [pageRows, getRowId]
  );
  const allPageSelected = multiSelect && pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const somePageSelected = multiSelect && !allPageSelected && pageIds.some((id) => selected.has(id));

  const toggleSelectAllOnPage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        pageIds.forEach((id) => next.delete(id));
      } else {
        pageIds.forEach((id) => next.add(id));
      }
      if (onSelectionChange) {
        const selRows = processedRows.filter((r, i) => next.has(getRowId(r, i)));
        onSelectionChange([...next], selRows);
      }
      return next;
    });
  };

  const toggleRow = (row, index) => {
    const id = getRowId(row, index);
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      if (onSelectionChange) {
        const selRows = processedRows.filter((r, i) => next.has(getRowId(r, i)));
        onSelectionChange([...next], selRows);
      }
      return next;
    });
  };

 const isInteractive = (el) => {
   if (!el) return false;
   return el.closest?.(
     'input, textarea, select, button, a, [role="button"], [contenteditable="true"],' +
       ".MuiCheckbox-root, .MuiSwitch-root, .MuiSlider-root, .MuiIconButton-root," +
       ".MuiSelect-select"
   );
 };

/* ---------- render ---------- */
  return (
    <Box>
      {/* Toolbar */}
      <>
        {showGlobalSearch && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ flex: 1 }}
          >
            <TextField
              size={isMobile || dense ? "small" : "medium"}
              placeholder="Search…"
              value={globalQuery}
              onChange={(e) => setGlobalQuery(e.target.value)}
              autoComplete="off"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />

            {(debouncedQuery || Object.values(filters).some(Boolean)) && (
              <Button
                onClick={clearFilters}
                variant="outlined"
                color="inherit"
                startIcon={<ClearIcon />}
                size={isMobile || dense ? "small" : "medium"}
              >
                Clear
              </Button>
            )}
          </Stack>
        )}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            justifyContent: !showGlobalSearch ? "flex-end" : "space-between",
          }}
          alignItems="center"
        >
          {showPagination && (
            <Select
              size={isMobile || dense ? "small" : "medium"}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[5, 10, 25, 50, 100].map((n) => (
                <MenuItem key={n} value={n}>
                  {n} / page
                </MenuItem>
              ))}
            </Select>
          )}

          <Tooltip title="Export CSV">
            <span>
              <IconButton
                color="primary"
                onClick={() =>
                  exportToExcel(rows, pageType, "promotionAutomation")
                }
              >
                <DownloadIcon />
              </IconButton>
            </span>
          </Tooltip>

          {/* <Tooltip title="Print">
            <span>
              <IconButton color="success" onClick={() => printTable(tableRef)}>
                <PrintIcon />
              </IconButton>
            </span>
          </Tooltip> */}
        </Stack>
      </>
      {/* Table */}
      <Paper variant="outlined" sx={{ mt: 0.5, overflow: "hidden" }}>
        <TableContainer
          sx={{
            height: height,
          }}
        >
          <Table stickyHeader size={dense ? "small" : "medium"} ref={tableRef}>
            <TableHead sx={{ position: "relative" }}>
              <TableRow>
                {/* ✅ selection header cell */}
                {multiSelect && (
                  <HeaderCell
                    padding="checkbox"
                    sx={{ width: 48, textAlign: "center" }}
                  >
                    <Checkbox
                      indeterminate={somePageSelected}
                      checked={allPageSelected}
                      onChange={toggleSelectAllOnPage}
                      inputProps={{ "aria-label": "select all on page" }}
                    />
                  </HeaderCell>
                )}

                {columns.map((col) => (
                  <HeaderCell
                    key={col.key}
                    style={{ textAlign: col.align || "left" }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      spacing={0.75}
                      sx={{ cursor: col.sortable ? "pointer" : "default" }}
                      onClick={() => {
                        if (!col.sortable) return;
                        setSort((prev) => {
                          if (prev.key !== col.key)
                            return { key: col.key, dir: "asc" };
                          if (prev.dir === "asc")
                            return { key: col.key, dir: "desc" };
                          return { key: null, dir: null };
                        });
                      }}
                    >
                      <Typography sx={{ width: col.width, fontWeight: 700 }}>
                        {col.header}
                      </Typography>
                      {sortIcon(col)}
                    </Stack>
                  </HeaderCell>
                ))}
              </TableRow>

              {/* filter row */}
              {showFilters && (
                <TableRow sx={{ position: "sticky", top: "27px" }}>
                  {multiSelect && (
                    <TableCell
                      padding="checkbox"
                      sx={{
                        backgroundColor: "action.hover",
                        padding:"5px !important",
                      }}
                    />
                  )}
                  {columns.map((col) => (
                    <TableCell
                      key={col.key}
                      sx={{
                        backgroundColor: "white",
                        border: "none !important",
                        padding:"5px !important",
                      }}
                    >
                      <Collapse in={showFilters} timeout={300} unmountOnExit>
                        {col.filterable ? (
                          <TextField
                            size="small"
                            fullWidth
                            placeholder={`Filter ${col.header}`}
                            value={filters[col.key]}
                            onChange={(e) =>
                              setFilters((s) => ({
                                ...s,
                                [col.key]: e.target.value,
                              }))
                            }
                            autoComplete="off"
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">
                            —
                          </Typography>
                        )}
                      </Collapse>
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableHead>

            <TableBody>
              {pageRows?.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={columns.length + (multiSelect ? 1 : 0)}
                    align="center"
                    sx={{ py: 6, color: "text.secondary" }}
                  >
                    No results found.
                  </TableCell>
                </TableRow>
              )}

              {pageRows?.map((row, idx) => {
                const id = getRowId(row, idx + page * pageSize);
                const isChecked = multiSelect && selected.has(id);
                return (
                  <TableRow
                    hover
                    key={id}
                    onClick={(e) => {
                      if (isInteractive(e.target)) return; 
                      onRowClick?.(row);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isInteractive(e.target)) {
                        onRowClick?.(row);
                      }
                    }}
                    sx={{
                      "&:nth-of-type(odd)": {
                        backgroundColor: "rgba(37,99,235,.04)",
                      },
                    }}
                  >
                    {multiSelect && (
                      <TableCell
                        padding="checkbox"
                        align="center"
                        onClick={(e) => e.stopPropagation()}
                        sx={{ padding: "5px !important" }}
                      >
                        <Checkbox
                          checked={!!isChecked}
                          onChange={() => toggleRow(row, idx + page * pageSize)}
                          inputProps={{ "aria-label": `select row ${id}` }}
                        />
                      </TableCell>
                    )}

                    {columns.map((col) => (
                      <TableCell
                        sx={{
                          maxWidth: `${col.width}px !important`,
                          padding: "5px !important",
                          whiteSpace: "nowrap", 
                          overflow: "hidden", 
                          textOverflow: "ellipsis",
                        }}
                        key={col.key}
                        align={col.align || "left"}
                        className={col.numeric ? "numeric" : ""}
                      >
                        {col.render ? col.render(row) : row[col.key] ?? ""}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {showPagination && (
          <Stack
            direction={{ xs: "column", sm: "row" }}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
            sx={{ px: 2, py: 1 }}
            spacing={1}
          >
            <Typography variant="body2" color="text.secondary">
              Showing{" "}
              <b>{processedRows.length === 0 ? 0 : page * pageSize + 1}</b>–
              <b>{Math.min((page + 1) * pageSize, processedRows.length)}</b> of{" "}
              <b>{processedRows.length}</b>
            </Typography>
            <TablePagination
              component="div"
              count={processedRows.length}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={pageSizeOptions}
            />
          </Stack>
        )}
      </Paper>
    </Box>
  );

}

export default CustomDatagrid;